import { createStyles, Divider, Stack } from '@mantine/core';
import { IconButton } from '@repo/foundations';
import { memo, useCallback } from 'react';
import type { ReactFlowInstance } from 'reactflow';

import type { Edge } from '@xyflow/react';
import type { LineageFlowEdge } from '../EntityEdge/EntityEdge.types';
import { type LineageFlowNode } from '../EntityNode/EntityNode.types';
import type { ERDFlowNode } from '../TableNode/TableNode.types';
import { type LineageFlowTemporaryNode } from '../TemporaryNode/TemporaryNode.types';

const useStyles = createStyles((theme) => ({
	wrapper: {
		position: 'absolute',
		width: 'fit-content',
		right: theme.spacing.md,
		bottom: theme.spacing.lg,
		gap: theme.spacing.xs,
		zIndex: 5,
	},
	zoomInButton: {
		borderTopLeftRadius: theme.radius.sm,
		borderTopRightRadius: theme.radius.sm,
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		boxShadow:
			'0px 1px 0px 0px #DDD inset, 1px 0px 0px 0px #DDD inset, -1px 0px 0px 0px #DDD inset',
	},
	zoomOutButton: {
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
		borderBottomLeftRadius: theme.radius.sm,
		borderBottomRightRadius: theme.radius.sm,
		boxShadow:
			'1px 0px 0px 0px #DDD inset, -1px 0px 0px 0px #DDD inset, 0px -1px 0px 0px #CCC inset',
	},
}));

interface ControlsProps {
	reactFlowInstance:
		| ReactFlowInstance<
				LineageFlowNode | LineageFlowTemporaryNode,
				LineageFlowEdge
		  >
		| ReactFlowInstance<ERDFlowNode, Edge>
		| undefined
		| null;
}

function Controls({ reactFlowInstance }: ControlsProps) {
	const { classes } = useStyles();

	// Memoize callbacks to prevent recreation on every render
	const handleFitView = useCallback(() => {
		reactFlowInstance?.fitView();
	}, [reactFlowInstance]);

	const handleZoomIn = useCallback(() => {
		reactFlowInstance?.zoomIn();
	}, [reactFlowInstance]);

	const handleZoomOut = useCallback(() => {
		reactFlowInstance?.zoomOut();
	}, [reactFlowInstance]);

	if (!reactFlowInstance) {
		return null;
	}

	return (
		<Stack className={classes.wrapper}>
			<IconButton
				iconName="focusCentered"
				tooltip="Fit view"
				onClick={handleFitView}
			/>
			<Stack spacing={0}>
				<IconButton
					className={classes.zoomInButton}
					iconName="plus"
					tooltip="Zoom in"
					onClick={handleZoomIn}
				/>
				<Divider color={'#DDD'} />
				<IconButton
					className={classes.zoomOutButton}
					iconName="minus"
					tooltip="Zoom out"
					onClick={handleZoomOut}
				/>
			</Stack>
		</Stack>
	);
}

// Memoize Controls to prevent re-renders when parent re-renders but props haven't changed
export default memo(Controls);
